import React, { useEffect, useRef } from 'react';
import { Field, FieldArray, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { get, merge, pick, startCase, omit, isEmpty } from 'lodash';
import * as Yup from 'yup';
import path from 'path-browserify';
import PropTypes from 'prop-types';
import { DefaultButton, IconButton, ActionButton } from 'office-ui-fabric-react/lib/Button';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Check } from 'office-ui-fabric-react/lib/Check';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { getFileTypeIconProps } from '@uifabric/file-type-icons';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import {
  Dialog,
  DialogFooter,
  Stack,
  PrimaryButton,
  Calendar,
  DateRangeType,
} from 'office-ui-fabric-react';
import {
  DetailsHeader,
  DetailsList,
  DetailsRow,
  SelectionMode,
} from 'office-ui-fabric-react/lib/DetailsList';
import clsx from 'clsx';
import { Label } from 'office-ui-fabric-react/lib/Label';
import moment from 'moment';
import { useBoolean } from '@uifabric/react-hooks';
import StackGrid from '../StackGrid';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import Dropdown from '../Dropdown';
import TextField, { numWithCommasFormatter } from '../TextField';
import DatePicker from '../DatePicker';
import { adjustmentTypes, taxCodes } from '../../constants';
import Checkbox from '../Checkbox';
import styles from './AdjustmentForm.style';
import DocumentUpload from '../DocumentUpload';
import SectionHeader from '../SectionHeader';
import TextButton from '../TextButton';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import ProductAutosuggest from '../ProductAutosuggest';
import ContractTypeAhead from '../../components/ContractTypeAhead';
import GraphUsersTypeAhead from '../../components/GraphUsersTypeAhead';
import Link from '../../components/Link';
import { fetchUsersByRoles, useUserSelectorByRoles } from '../../actions/users';
import { fetchContract } from '../../actions/contracts';
import { fetchForex } from '../../actions/forex';
import { fetchKeywordsByArea } from '../../actions/keywords';
import MultiPickerFormik from '../../components/MultiPicker/MultiPickerFormik';

import { showFlashMessage } from '../../actions/ui';
import {
  createAdjustment,
  editAdjustment,
  saveReversalAdjustmentDraft,
  submitAdjustment,
  submitExistingAdjustment,
  submitReversalAdjustment,
  uploadAdjustmentAttachment,
  saveAdjustmentMeta,
} from '../../actions/adjustments';
import {
  getAdvancePaymentTotalString,
  getTotalPayment,
  getLineItems,
  getAdjustmentListItemsName,
  getPaymentAmountNumber,
  formatDate,
} from '../../utils/adjustmentUtils';
import GeographyTypeAhead from '../GeographyTypeAhead/GeographyTypeAhead';
import CurrencyTypeAhead from '../CurrencyTypeAhead';
import { fetchAdvanceGroups } from '../../actions/advanceGroups';
import { formatDateString } from '../../utils';
import { MonthPickerStrings } from '../DatePicker/MonthPickerStrings';

const adjustmentValidationSchema = Yup.object().shape({
  name: Yup.string().max(100).required('Required').label('Name'),
  contract: Yup.object({
    contractId: Yup.string().required('Required'),
  }),
  adjustmentType: Yup.string().required('Required'),
  submitButtonType: Yup.string(),
  autoReverse: Yup.boolean(),
  reverseDate: Yup.date().nullable(),
  comments: Yup.object({
    businessJustification: Yup.string(),
    statementComment: Yup.string(),
  }),
  reviewer: Yup.object({
    reviewerId: Yup.string(),
    reviewerJustification: Yup.string(),
  }),
  approver: Yup.object().when('submitButtonType', {
    is: submitButtonType => submitButtonType === 'Submit',
    then: Yup.object({
      approverId: Yup.string().required('Required'),
      approverJustification: Yup.string(),
    }),
  }),
  advanceGroup: Yup.string().when(['adjustmentType', 'submitButtonType'], {
    is: (adjustmentType, submitButtonType) =>
      (adjustmentType === 'RecoupablePayment' || adjustmentType === 'AdvanceBalance') &&
      submitButtonType === 'Submit',
    then: Yup.string().required('Required'),
  }),
  adjustments: Yup.array()
    .when(['submitButtonType', 'contract'], {
      is: (submitButtonType, contract) =>
        submitButtonType === 'Submit' && contract.silo === '1p-azure',
      then: Yup.array().of(
        Yup.object({
          product: Yup.object({
            key: Yup.mixed().required('Required'),
          }),
          billToGeography: Yup.string().required('Required'),
          geography: Yup.string().required('Required'),
          currency: Yup.string().required('Required'),
          amount: Yup.string().nullable().required('Required'),
          actualDate: Yup.date().nullable().required('Required'),
        }),
      ),
    })
    .when(['submitButtonType', 'contract'], {
      is: (submitButtonType, contract) =>
        submitButtonType === 'Submit' && contract.silo !== '1p-azure',
      then: Yup.array().of(
        Yup.object({
          product: Yup.object({
            key: Yup.mixed().required('Required'),
          }),
          geography: Yup.string().required('Required'),
          currency: Yup.string().required('Required'),
          amount: Yup.string().nullable().required('Required'),
          actualDate: Yup.date().nullable().required('Required'),
        }),
      ),
    }),
  recoupablePayments: Yup.array().when('submitButtonType', {
    is: submitButtonType => submitButtonType === 'Submit',
    then: Yup.array().of(
      Yup.object({
        invoiceNumber: Yup.string().required('Required'),
        amount: Yup.string().required('Required'),
        currency: Yup.string().required('Required'),
        product: Yup.object({
          key: Yup.mixed(),
        }).nullable(),
        invoiceDate: Yup.date().required('Required'),
        statementPeriod: Yup.date().required('Required'),
        dueDate: Yup.date().required('Required'),
        taxCode: Yup.string().required('Required'),
        description: Yup.string(),
        statementComment: Yup.string(),
        invoiceApprover: Yup.string(),
        interimInvoiceApprover: Yup.string(),
      }),
    ),
  }),
  nonRecoupablePayments: Yup.array().when('submitButtonType', {
    is: submitButtonType => submitButtonType === 'Submit',
    then: Yup.array().of(
      Yup.object({
        invoiceNumber: Yup.string().required('Required'),
        amount: Yup.string().required('Required'),
        currency: Yup.string().required('Required'),
        product: Yup.object({
          key: Yup.mixed(),
        }).nullable(),
        invoiceDate: Yup.date().required('Required'),
        dueDate: Yup.date().required('Required'),
        taxCode: Yup.string().required('Required'),
        expenseAccount: Yup.string().required('Required'),
        costCenter: Yup.string().required('Required'),
        description: Yup.string(),
        invoiceApprover: Yup.string(),
        interimInvoiceApprover: Yup.string(),
      }),
    ),
  }),
  advanceBalanceAdjustments: Yup.array().when('submitButtonType', {
    is: submitButtonType => submitButtonType === 'Submit',
    then: Yup.array().of(
      Yup.object({
        adjustmentDate: Yup.date().required('Required'),
        amount: Yup.string().required('Required'),
        currency: Yup.string().required('Required'),
        product: Yup.object({
          key: Yup.mixed(),
        }).nullable(),
        description: Yup.string(),
        statementComment: Yup.string(),
      }),
    ),
  }),
});

function createNewAdjustmentLineItem(overrides) {
  return {
    product: { key: null },
    geography: '',
    currency: '',
    amount: null,
    actualDate: null,
    ...overrides,
  };
}

function createNewRecoupablePayment(overrides) {
  return {
    invoiceNumber: '',
    amount: '',
    currency: '',
    product: null,
    invoiceDate: '',
    statementPeriod: '',
    dueDate: '',
    description: '',
    statementComment: '',
    ...overrides,
  };
}

function createNewNonRecoupablePayment(overrides) {
  return {
    invoiceNumber: '',
    amount: '',
    currency: '',
    product: null,
    invoiceDate: '',
    dueDate: '',
    taxCode: '',
    expenseAccount: '',
    costCenter: '',
    description: '',
    invoiceApprover: '',
    ...overrides,
  };
}

function createNewAdvanceBalanceAdjustment(overrides) {
  return {
    invoiceNumber: '',
    amount: '',
    currency: '',
    product: null,
    description: '',
    statementComment: '',
    ...overrides,
  };
}

function createNewAdjustmentLineOrPaymentItem(adjustmentType, overrides) {
  switch (adjustmentType) {
    case 'AdjustmentToRoyaltiesEarned':
      return createNewAdjustmentLineItem(overrides);
    case 'RecoupablePayment':
      return createNewRecoupablePayment(overrides);
    case 'NonRecoupablePayment':
      return createNewNonRecoupablePayment(overrides);
    case 'AdvanceBalance':
      return createNewAdvanceBalanceAdjustment(overrides);
    default:
      return null;
  }
}

const getProductInput = lineItem => {
  if (lineItem.productDetails) return lineItem.productDetails.name;
  if (lineItem.pfamDetails) return lineItem.pfamDetails.productFamilyName;
  return undefined;
};

const formatToLocalDate = date => (date ? moment.utc(date).local(true).toDate() : '');

const transformLineItem = (lineItem, allLineItems) => {
  const foundReversal = allLineItems.find(
    li => li.reversal && li.reversal.lineItemId === lineItem._id,
  );
  const checked = !!foundReversal;
  const disabled = !!foundReversal && foundReversal.status.toLowerCase() !== 'draft';

  return {
    ...lineItem,
    checked,
    disabled,
    product: {
      key: lineItem.product,
      text: getProductInput(lineItem),
    },
    amount: lineItem.amount?.toLocaleString(),
    actualDate: formatToLocalDate(lineItem.actualDate),
  };
};

const transform = (adjustment, mode) => ({
  ...adjustment,
  reverseDate: formatToLocalDate(adjustment.reverseDate),
  adjustments: adjustment.lineItems
    ?.map(item => transformLineItem(item, adjustment.lineItems))
    // when in copy mode, remove the reversal line items
    // eslint-disable-next-line no-use-before-define
    .filter(lineItem => !(mode === AdjustmentForm.MODE.COPY && lineItem.reversal)),
  adjustmentType: adjustment.type,
  keywords: adjustment.keywords?.map(keyword => ({
    key: keyword,
    text: keyword,
  })),
});

const transformPaymentItem = lineItem => {
  return {
    ...lineItem,
    amount: lineItem.amount?.toLocaleString(),
    invoiceApprover: {
      ...lineItem.invoiceApprover,
      displayName: lineItem.invoiceApprover?.name,
    },
    interimInvoiceApprover: {
      ...lineItem.interimInvoiceApprover,
      displayName: lineItem.interimInvoiceApprover?.name,
    },
    product: {
      key: lineItem.product,
      text: getProductInput(lineItem),
    },
    dueDate: formatToLocalDate(lineItem.dueDate),
    invoiceDate: formatToLocalDate(lineItem.invoiceDate),
    statementPeriod: lineItem.statementPeriod ? lineItem.statementPeriod : '',
    adjustmentDate: formatToLocalDate(lineItem.adjustmentDate),
  };
};

const transformAdvanceAdjustment = adjustment => {
  const transformedAdjustment = {
    ...adjustment,
    adjustmentType: adjustment.type,
    advanceGroup: adjustment.advanceGroupId,
    keywords: adjustment.keywords?.map(keyword => ({
      key: keyword,
      text: keyword,
    })),
  };
  const adjustmentListItemsName = getAdjustmentListItemsName(adjustment.type);
  transformedAdjustment[adjustmentListItemsName] = adjustment.lineItems?.map(item =>
    transformPaymentItem(item),
  );

  return transformedAdjustment;
};

const AdjustmentReviewerRoles = [
  'businessProcessingManager',
  'royaltyAnalyst',
  'royaltyOperationsManager',
  'contractProcessingManager',
  'royaltyPayments',
];
const AdvanceAdjustmentCpmApproverRoles = ['contractProcessingManager'];
const AdvanceAdjustmentRomApproverRoles = ['royaltyOperationsManager'];

const amountThreshold = 3000000;
const currentMonth = new Date().getUTCMonth() + 1;
const currentYear = new Date().getUTCFullYear();
const keywordArea = 'adjustments';

const AdjustmentForm = React.forwardRef(
  ({ adjustmentId, onSubmit, onSave, onSuccess, onFailure, mode, contractAddress }, ref) => {
    const adjustment = useSelector(state =>
      get(state, `entities.adjustments.${adjustmentId}`, null),
    );
    const adjustmentContract = useSelector(state =>
      get(state, `entities.contracts.${contractAddress}`, null),
    );

    const forexRates = useSelector(state =>
      get(state, `entities.forex.${currentMonth}-${currentYear}`, null),
    );
    let forexMap;
    if (forexRates?.rates) {
      forexMap = new Map(forexRates.rates.map(i => [i.currency, i.rate]));
    }
    const keywords = useSelector(state => get(state, `entities.keywords.${keywordArea}`, null));

    const uploadref = useRef(null);
    const dispatch = useDispatch();
    const [dialogHidden, { setTrue: hideDialog, setFalse: showDialog }] = useBoolean(true);
    let skipInvoiceValidation = false;

    const reviewers = useUserSelectorByRoles(AdjustmentReviewerRoles).map(user => ({
      key: user.oid,
      text: user.displayName,
    }));

    if (reviewers?.length > 0) {
      reviewers.unshift({ key: '', text: 'Select' }); // Adding this , so that we have the option to unset the reviewer
    }

    const advanceAdjustmentCpmApprovers = useUserSelectorByRoles(
      AdvanceAdjustmentCpmApproverRoles,
    ).map(user => ({
      key: user.oid,
      text: user.displayName,
    }));
    const advanceAdjustmentRomApprovers = useUserSelectorByRoles(
      AdvanceAdjustmentRomApproverRoles,
    ).map(user => ({
      key: user.oid,
      text: user.displayName,
    }));

    const advanceGroupMap = useSelector(state => get(state, `entities.advancegroups`, null));
    const advanceGroupOptions = useSelector(state =>
      get(state, 'pagination.advancegroups.results', []).map(_id => advanceGroupMap[_id]),
    ).map(advanceGroup => ({
      key: advanceGroup._id,
      text: advanceGroup.name,
    }));

    useEffect(() => {
      dispatch(
        fetchUsersByRoles([
          ...AdjustmentReviewerRoles,
          ...AdvanceAdjustmentCpmApproverRoles,
          ...AdvanceAdjustmentRomApproverRoles,
        ]),
      );
    }, [dispatch]);

    useEffect(() => {
      if (adjustment?.contract.contractId) {
        dispatch(fetchAdvanceGroups({ contractId: adjustment?.contract.contractId }));
      }
    }, [dispatch]);

    useEffect(() => {
      dispatch(fetchForex(currentMonth, currentYear));
    }, []);

    useEffect(() => {
      if (adjustment?.contract.silo) {
        dispatch(fetchKeywordsByArea(keywordArea, adjustment?.contract.silo));
      }
    }, []);

    useEffect(() => {
      if (contractAddress) {
        dispatch(fetchContract(contractAddress, { useCache: false }));
      }
    }, [dispatch, contractAddress]);

    const initialValues = merge(
      {
        name: '',
        contract: {
          contractId: '',
          ...adjustmentContract,
        },
        adjustmentType: '',
        autoReverse: false,
        reverseDate: null,
        comments: {
          businessJustification: '',
          statementComment: '',
        },
        keywords: [],
        adjustments: [],
        recoupablePayments: [],
        nonRecoupablePayments: [],
        advanceBalanceAdjustments: [],
        attachments: [],
        submitButtonType: '',
        reviewer: {
          reviewerId: '',
          reviewerJustification: '',
        },
        approver: {
          approverId: '',
          approverJustification: '',
        },
      },
      adjustment &&
        adjustment.type === 'AdjustmentToRoyaltiesEarned' &&
        transform(adjustment, mode),
      adjustment &&
        adjustment.type !== 'AdjustmentToRoyaltiesEarned' &&
        transformAdvanceAdjustment(adjustment, mode),
    );

    const attachmentColumns = ({ remove }) => [
      {
        key: 'fileIcon',
        name: 'File Type',
        iconName: 'Page',
        isIconOnly: true,
        fieldName: 'icon',
        minWidth: 16,
        maxWidth: 16,
        onRender(attachment) {
          if (attachment.isUploading) {
            return <Spinner size={SpinnerSize.small} className={styles.loadingSpinner} />;
          }

          const extension = path.extname(attachment.displayName);

          return (
            <FontIcon {...getFileTypeIconProps({ extension, size: 16, imageFileType: 'png' })} />
          );
        },
      },
      {
        key: 'displayName',
        name: 'File Name',
        isRowHeader: true,
        isSorted: true,
        isSortedDescending: false,
        data: 'string',
        isPadded: true,
        onRender(attachment) {
          return <Text>{attachment.displayName}</Text>;
        },
      },
      {
        key: 'delete',
        name: 'Delete',
        fieldName: 'delete',
        minWidth: 30,
        maxWidth: 30,
        onRender(attachment, index) {
          return (
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              ariaLabel="Delete"
              className={clsx(
                styles.attachmentDeleteIcon,
                attachment.isUploading && styles.attachmentDeleteIconDisabled,
              )}
              onClick={async () => {
                if (attachment.isUploading) {
                  return;
                }

                remove(index);
              }}
            />
          );
        },
      },
    ];

    const createReversalLineItemsRequest = formLineItems =>
      formLineItems
        .filter(lineItem => !!lineItem.reversal)
        .filter(lineItem => lineItem.status.toLowerCase() === 'draft')
        .map(lineItem => ({
          id: lineItem.reversal.lineItemId,
          actualDate: lineItem.actualDate,
        }));

    const updateFormValuesBasedOnAdjustmentType = (formValues, values) => {
      if (values.adjustmentType !== 'AdjustmentToRoyaltiesEarned') {
        // eslint-disable-next-line no-param-reassign
        formValues = omit(formValues, ['autoReverse', 'reverseDate']);
      }

      if (
        values.adjustmentType === 'RecoupablePayment' ||
        values.adjustmentType === 'AdvanceBalance'
      ) {
        // eslint-disable-next-line no-param-reassign
        formValues = {
          ...formValues,
          advanceGroupId: values.advanceGroup,
        };
      }
      return formValues;
    };

    const doPaymentsHaveSameCurrency = payments => {
      const currency = payments[0]?.currency?.code || payments[0]?.currency;
      return !payments.some(payment => (payment.currency?.code || payment.currency) !== currency);
    };

    const isPaymentAmountGreaterThanThreshold = values => {
      const adjustmentListItemsName = getAdjustmentListItemsName(values.adjustmentType);
      const payments = values[adjustmentListItemsName];
      if (!doPaymentsHaveSameCurrency(payments)) {
        return false;
      }

      const currency = payments[0]?.currency?.code || payments[0]?.currency;
      const total = getTotalPayment(values[adjustmentListItemsName].map(getPaymentAmountNumber));
      if (currency === 'USD') {
        return total > amountThreshold;
      }
      if (forexMap) {
        const totalInUSD = forexMap.get(currency) * total;
        return totalInUSD > amountThreshold;
      }
      return false;
    };

    const getApproverAssignedToContract = values => {
      if (isPaymentAmountGreaterThanThreshold(values)) {
        /* When contract is fetched from Typeahead, metadata.royaltyOperationsManager has the oid value. 
         When contract is derived from contractAddress during contract adjustment navigation, metadata.royaltyOperationsManager.oid has the oid value */

        // eslint-disable-next-line no-nested-ternary
        return values.contract?.metadata?.royaltyOperationsManager?.oid
          ? values.contract?.metadata?.royaltyOperationsManager?.oid
          : values.contract?.metadata?.royaltyOperationsManager
          ? values.contract?.metadata?.royaltyOperationsManager
          : '';
      }
      // eslint-disable-next-line no-nested-ternary
      return values.contract?.metadata?.contractProcessingManager?.oid
        ? values.contract?.metadata?.contractProcessingManager?.oid
        : values.contract?.metadata?.contractProcessingManager
        ? values.contract?.metadata?.contractProcessingManager
        : '';
    };
    const getApprover = values => {
      if (adjustment && adjustment.approver?.approverId) {
        return adjustment.approver.approverId;
      }

      return getApproverAssignedToContract(values);
    };

    // ApproverJustification is required, when user selects any other Approver other than the contract's CPM/ROM
    const validateApproverJustification = values => {
      const contractApprover = getApproverAssignedToContract(values);
      if (
        !values.approver.approverJustification &&
        values.approver.approverId !== contractApprover
      ) {
        return { approver: { approverJustification: 'Required' } };
      }

      return {};
    };

    // Adding some custom validations on the form (apart from the schema level validations)
    const areThereAnyCustomValidationErrors = (values, setErrors, setSubmitting) => {
      const approverErrors = validateApproverJustification(values);
      if (!isEmpty(approverErrors)) {
        setErrors({ ...approverErrors });
        setSubmitting(false);
        return false;
      }
      return true;
    };

    const submitHandler = async (values, { setErrors, setSubmitting }) => {
      let response;

      // Handle Custom Validation
      if (
        values.submitButtonType === 'Submit' &&
        !areThereAnyCustomValidationErrors(values, setErrors, setSubmitting)
      ) {
        return {};
      }

      // POST form data to api
      const formLineItems = getLineItems(values, values.adjustmentType);

      const formAttachments = values.attachments.map(attachment => attachment.filePath);

      let formValues = {
        contractId: values.contract.contractId,
        type: values.adjustmentType,
        lineItems: formLineItems,
        attachments: formAttachments,
        reviewer: pick(values.reviewer, ['reviewerId', 'reviewerJustification']),
        approver: pick(values.approver, ['approverId', 'approverJustification']),
        reverseDate: formatDate(values.reverseDate),
        ...pick(values, ['name', 'autoReverse', 'comments']),
        keywords: values.keywords?.map(keyword => keyword.key),
      };

      if (skipInvoiceValidation) {
        formValues = {
          ...formValues,
          skipInvoiceValidation,
        };
      }

      formValues = updateFormValuesBasedOnAdjustmentType(formValues, values);

      switch (values.submitButtonType) {
        case 'Save':
          switch (mode) {
            case AdjustmentForm.MODE.CREATE:
            case AdjustmentForm.MODE.COPY:
              response = await dispatch(createAdjustment(formValues));
              break;
            case AdjustmentForm.MODE.EDIT:
              response = await dispatch(editAdjustment(formValues, values._id));
              break;
            case AdjustmentForm.MODE.REVERSE:
              response = await dispatch(
                saveReversalAdjustmentDraft(
                  {
                    lineItems: createReversalLineItemsRequest(values.adjustments),
                    attachments: formAttachments,
                    reviewer: values.reviewer,
                  },
                  values._id,
                ),
              );
              break;
            case AdjustmentForm.MODE.MINOR:
              response = await dispatch(
                saveAdjustmentMeta(adjustmentId, {
                  statementComment: values?.comments?.statementComment,
                }),
              );
              break;
            default:
              break;
          }
          break;
        case 'Submit':
          switch (mode) {
            case AdjustmentForm.MODE.CREATE:
            case AdjustmentForm.MODE.COPY:
              response = await dispatch(submitAdjustment(formValues));
              break;
            case AdjustmentForm.MODE.EDIT:
              response = await dispatch(submitExistingAdjustment(formValues, values._id));
              break;
            case AdjustmentForm.MODE.REVERSE:
              response = await dispatch(
                submitReversalAdjustment(
                  {
                    lineItems: createReversalLineItemsRequest(values.adjustments),
                    attachments: formAttachments,
                    reviewer: values.reviewer,
                  },
                  values._id,
                ),
              );
              break;
            default:
              break;
          }
          break;
        default:
          return {};
      }

      // Display error messages from api , if any, on the UI
      if (response.error && response.payload.message) {
        const error = new Error(response.payload.message);
        error.payload = response.payload.message;
        if (response.payload.message === 'INVOICE-VALIDATION-ERROR') {
          setErrors({
            form: error.message,
            adjustmentsWithDuplicateInvoices: response.payload.body?.adjustments,
          });
          showDialog();
        } else {
          setErrors({ form: error.message });
        }

        await onFailure(response);
        return response;
      }

      // Get the response and pass it back to onSuccess callback
      response = get(
        response,
        `payload.entities.adjustments.${get(response, 'payload.result')}`,
        {},
      );
      await onSuccess(response);

      return response;
    };

    const handleUpload =
      ({ unshift, replace, remove }) =>
      async files => {
        if (files.length === 0) {
          return;
        }

        // add the element to the list
        unshift({
          isUploading: true,
          displayName: files[0].name,
        });

        const response = await dispatch(uploadAdjustmentAttachment(files));

        if (response.error) {
          // handle error

          remove(0);

          dispatch(
            showFlashMessage(response.payload?.message || 'Error uploading file', {
              type: 'error',
              duration: 10000,
            }),
          );
        } else {
          replace(0, {
            isUploading: false,
            displayName: files[0].name,
            filePath: response.payload.files[0].filePath,
          });

          dispatch(
            showFlashMessage('Successfully uploaded attachment.', {
              type: 'success',
              duration: 10000,
            }),
          );
        }
      };

    const getAdjustmentsFieldName = (column, rowIndex) => {
      return `adjustments.${rowIndex}.${column.key}`;
    };

    const getAdvanceAdjustmentFieldName = (field, rowIndex, adjustmentListItemsName) => {
      return `${adjustmentListItemsName}.${rowIndex}.${field}`;
    };

    // Required so that we can create the first line item based on the contract currency per business requirement
    const addInitialLineItem = (values, setFieldValue, setFieldTouched) => {
      const adjustmentListItemsName = getAdjustmentListItemsName(values.adjustmentType);
      if (values[adjustmentListItemsName].length === 0) {
        setFieldValue(adjustmentListItemsName, [
          createNewAdjustmentLineOrPaymentItem(values.adjustmentType, {
            currency: { code: values.contract.contractCurrency },
          }),
        ]);
        setFieldTouched(adjustmentListItemsName);
      }
      return true;
    };

    // When users switch adjustment types, we delete the errors corresponding to the non-selected types
    const deleteNonSelectedAdjustmentTypeItemErrors = (values, errors) => {
      if (errors && Object.keys(errors).length !== 0) {
        adjustmentTypes.forEach(adjustmentType => {
          if (adjustmentType.key !== values.adjustmentType) {
            const adjustmentListItemsName = getAdjustmentListItemsName(adjustmentType.key);
            // eslint-disable-next-line no-param-reassign
            delete errors[adjustmentListItemsName];
          }
        });
      }
    };

    const contractChangeCallback = (contract, errors, setFieldValue, setFieldTouched, values) => {
      // Fetch advancegroups associated to the contract and set to recoupment terms
      dispatch(fetchAdvanceGroups({ contractId: contract.contractId }));
      dispatch(fetchKeywordsByArea(keywordArea, contract.silo));
      setFieldValue('advanceAccount', undefined);
      // eslint-disable-next-line no-param-reassign
      setFieldValue(getAdjustmentListItemsName(values.adjustmentType), []);
    };

    const advanceGroupChangeCallback = (
      values,
      errors,
      setFieldValue,
      setFieldTouched,
      selectedAdvanceGroup,
    ) => {
      const advanceAccount = advanceGroupMap[selectedAdvanceGroup].accountId;
      setFieldValue('advanceAccount', advanceAccount);
    };

    const addAdvanceAccount = (values, setFieldValue) => {
      if (
        !values.advanceAccount &&
        values.advanceGroup &&
        advanceGroupMap &&
        advanceGroupMap[values.advanceGroup]
      ) {
        setFieldValue('advanceAccount', advanceGroupMap[values.advanceGroup].accountId);
      }
      return true;
    };

    return (
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={adjustmentValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit || onSave || submitHandler}
      >
        {({ errors, isSubmitting, values, setFieldValue, setFieldTouched, submitForm }) => (
          <Form>
            {errors.form && !errors.adjustmentsWithDuplicateInvoices && (
              <MessageBar
                messageBarType={MessageBarType.error}
                messageBarIconProps={{ iconName: 'Info' }}
                isMultiline
                dismissButtonAriaLabel="Close"
                nowrap
              >
                {errors.form}
              </MessageBar>
            )}

            {errors.form && errors.adjustmentsWithDuplicateInvoices && (
              <Dialog hidden={dialogHidden} onDismiss={hideDialog}>
                <Label>
                  The combination of <span className={styles.bold}>Invoice Number</span> and{' '}
                  <span className={styles.bold}>Amount</span> has been flagged as possible
                  duplicate.Would you like to{' '}
                  {values.submitButtonType === 'Submit' ? 'Submit' : 'Save'} anyway ?
                </Label>

                <Label>
                  <span className={styles.bold}>Previous Submission(s):</span>
                </Label>
                {errors.adjustmentsWithDuplicateInvoices.map(duplicateAdjustment =>
                  duplicateAdjustment.lineItems.map(lineItem => (
                    <div className={styles.topPadding}>
                      <div>
                        Adjustment Name:{' '}
                        <span className={styles.bold}>{duplicateAdjustment.name}</span>
                      </div>
                      <div>
                        Created Date:{' '}
                        <span span className={styles.bold}>
                          {formatDateString(duplicateAdjustment.createdTimestamp)}
                        </span>
                      </div>
                      <div>
                        Created By:{' '}
                        <span span className={styles.bold}>
                          {duplicateAdjustment.creator?.name}
                        </span>
                      </div>
                      <div>
                        Invoice Number:{' '}
                        <span className={styles.bold}>{lineItem.invoiceNumber}</span>
                      </div>
                      <div>
                        Invoice Date:{' '}
                        <span span className={styles.bold}>
                          {formatDateString(lineItem.invoiceDate)}
                        </span>
                      </div>
                      <div>
                        Amount:{' '}
                        <span span className={styles.bold}>
                          {lineItem.amount}
                        </span>
                      </div>
                      <div>
                        Currency:{' '}
                        <span span className={styles.bold}>
                          {lineItem.currency}
                        </span>
                      </div>
                    </div>
                  )),
                )}

                <DialogFooter>
                  <PrimaryButton
                    onClick={() => {
                      skipInvoiceValidation = true;
                      submitForm();
                    }}
                    text={values.submitButtonType === 'Submit' ? 'Submit' : 'Save'}
                  />
                  <DefaultButton onClick={hideDialog} text="Cancel" />
                </DialogFooter>
              </Dialog>
            )}

            {values.contract?.contractId &&
              values.adjustmentType &&
              addInitialLineItem(values, setFieldValue, setFieldTouched) &&
              addAdvanceAccount(values, setFieldValue) &&
              deleteNonSelectedAdjustmentTypeItemErrors(values, errors)}

            {/* Adjustment Details Module */}
            <Module>
              <ModuleContent>
                <SubModule disableAccordian>
                  <SubModuleHeader variant="large">Adjustment Details</SubModuleHeader>

                  <SubModuleContent>
                    <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                      <Field
                        name="name"
                        label="Adjustment Name"
                        component={TextField}
                        disabled={
                          isSubmitting ||
                          mode === AdjustmentForm.MODE.REVERSE ||
                          mode === AdjustmentForm.MODE.MINOR
                        }
                        callout="Adjustment name will be visible to Partners."
                        required
                        className={styles.adjustmentName}
                        data-testid="adjustmentFormName"
                      />

                      <Field
                        name="adjustmentType"
                        label="Adjustment Type"
                        options={adjustmentTypes}
                        component={Dropdown}
                        disabled={
                          isSubmitting ||
                          mode === AdjustmentForm.MODE.REVERSE ||
                          mode === AdjustmentForm.MODE.EDIT ||
                          mode === AdjustmentForm.MODE.MINOR
                        }
                        required
                        data-testid="adjustmentFormAdjustmentType"
                      />

                      <Stack horizontal gap={32}>
                        <Stack.Item grow={10} shrink={10}>
                          <Field
                            name="contract"
                            label="Contract"
                            component={ContractTypeAhead}
                            disabled={
                              isSubmitting ||
                              mode === AdjustmentForm.MODE.REVERSE ||
                              mode === AdjustmentForm.MODE.MINOR
                            }
                            required
                            data-testid="adjustmentFormContract"
                            handleCallback={contractChangeCallback}
                            contractName={values.contract.name}
                            value={values.contract.name}
                          />
                        </Stack.Item>

                        {values.contract && values.contract.contractId && (
                          <Stack.Item grow={2} shrink={2}>
                            <Stack gap={10}>
                              <Text>Contract ID</Text>
                              <Text className={styles.contractId}>
                                {values.contract.contractId}
                              </Text>
                            </Stack>
                          </Stack.Item>
                        )}
                      </Stack>

                      {values.adjustmentType === 'AdjustmentToRoyaltiesEarned' && (
                        <Stack horizontal verticalAlign="center" gap={32} shrink>
                          <Stack.Item grow={4}>
                            <Field
                              name="autoReverse"
                              label="Auto Reverse"
                              component={Checkbox}
                              disabled={
                                isSubmitting ||
                                mode === AdjustmentForm.MODE.REVERSE ||
                                mode === AdjustmentForm.MODE.MINOR
                              }
                              className={styles.autoReverse}
                              inputProps={{
                                'data-testid': 'adjustmentFormAutoReverse',
                              }}
                            />
                          </Stack.Item>
                          <Stack.Item grow={10}>
                            <>
                              <Label>Reverse Date</Label>
                              <Field
                                name="reverseDate"
                                component={DatePicker}
                                disabled={
                                  isSubmitting ||
                                  mode === AdjustmentForm.MODE.REVERSE ||
                                  mode === AdjustmentForm.MODE.MINOR
                                }
                                data-testid="adjustmentFormReverseDate"
                                showClearButton
                                ariaLabel="reverse date"
                              />
                            </>
                          </Stack.Item>
                        </Stack>
                      )}
                      <Field
                        name="comments.businessJustification"
                        label="Business Justification"
                        component={TextField}
                        disabled={
                          isSubmitting ||
                          mode === AdjustmentForm.MODE.REVERSE ||
                          mode === AdjustmentForm.MODE.MINOR
                        }
                        callout="Justification comments are internal only and will not be visible to partners."
                        multiline
                        data-testid="adjustmentFormBusinessJustification"
                      />
                      {values.adjustmentType === 'AdjustmentToRoyaltiesEarned' && (
                        <Field
                          name="comments.statementComment"
                          label="Statement Comment"
                          component={TextField}
                          disabled={isSubmitting || mode === AdjustmentForm.MODE.REVERSE}
                          callout="Statement comments will be visible to partners."
                          multiline
                          data-testid="adjustmentFormStatementComment"
                        />
                      )}

                      {keywords?.tags && keywords.tags.length > 0 && (
                        <Field
                          name="keywords"
                          label="Keywords"
                          component={MultiPickerFormik}
                          options={keywords?.tags.map(tag => ({
                            key: tag,
                            text: tag,
                          }))}
                          data-testid="keywords"
                          aria-label="keywords"
                          multiTag
                        />
                      )}
                    </StackGrid>

                    <div className={styles.attachments}>
                      <SectionHeader>Attachments</SectionHeader>

                      <FieldArray name="attachments">
                        {({ unshift, replace, remove }) => (
                          <>
                            <DetailsList
                              className={styles.transparentBackground}
                              columns={attachmentColumns({ replace, remove })}
                              items={values.attachments}
                              isHeaderVisible={false}
                              selectionMode={SelectionMode.none}
                              onRenderRow={props => (
                                <DetailsRow {...props} className={styles.transparentBackground} />
                              )}
                            />

                            <div>
                              <ActionButton
                                iconProps={{ iconName: 'Attach' }}
                                title="Attach"
                                ariaLabel="Attach"
                                disabled={isSubmitting || mode === AdjustmentForm.MODE.MINOR}
                                onClick={() => uploadref.current.click()}
                              >
                                Attach Documentation
                              </ActionButton>
                              <DocumentUpload
                                className={styles.uploader}
                                ref={uploadref}
                                onUpload={handleUpload({ unshift, replace, remove })}
                                accept=".*"
                                id="btn_adjustmentAttachmentUpload"
                                disabled={values.attachments.some(
                                  attachment => attachment.isUploading,
                                )}
                                data-testid="attachmentsUploadButton"
                              />
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </SubModuleContent>
                </SubModule>
              </ModuleContent>
            </Module>

            {/* Adjustment To Royalties Earned Module */}
            {values.adjustmentType === 'AdjustmentToRoyaltiesEarned' &&
              values.contract?.contractId && (
                <Module>
                  <ModuleContent>
                    <SubModule disableAccordian data-testid="adjustmentsRoyaltiesEarnedSection">
                      <SubModuleHeader variant="large">
                        {startCase(values.adjustmentType)}
                      </SubModuleHeader>
                      <SubModuleContent>
                        <FieldArray name="adjustments">
                          {({ remove, push, insert, replace }) => (
                            <>
                              <DetailsList
                                className={styles.royaltiesAdjustmentsList}
                                onRenderDetailsHeader={props => (
                                  <DetailsHeader
                                    {...props}
                                    className={styles.royaltiesEarnedColumn}
                                  />
                                )}
                                selectionMode={SelectionMode.none}
                                items={values.adjustments}
                                onRenderRow={props => {
                                  return (
                                    <DetailsRow
                                      {...props}
                                      className={styles.royaltiesEarnedColumn}
                                      data-testid="adjustmentFormLineItem"
                                    />
                                  );
                                }}
                                columns={[
                                  mode === AdjustmentForm.MODE.REVERSE && {
                                    key: 'reverse',
                                    name: 'Reverse?',
                                    headerClassName: styles.royaltiesEarnedColumn,
                                    minWidth: 60,
                                    maxWidth: 60,
                                    onRender(adjustmentLineItem, rowIndex, column) {
                                      const name = getAdjustmentsFieldName(column, rowIndex);

                                      if (adjustmentLineItem.reversal) {
                                        return null;
                                      }

                                      return (
                                        <DefaultButton
                                          className={styles.reverseCheck}
                                          data-testid="adjustmentLineItemReverseCheckMark"
                                          ariaLabel="reverse check"
                                          disabled={adjustmentLineItem.disabled}
                                          onClick={() => {
                                            const checked = !adjustmentLineItem.checked;

                                            // update checked status of the line item
                                            replace(rowIndex, {
                                              ...adjustmentLineItem,
                                              checked,
                                            });

                                            // update add/removal of line items
                                            if (checked) {
                                              insert(rowIndex + 1, {
                                                ...adjustmentLineItem,
                                                _id: `reversal:${adjustmentLineItem._id}`,
                                                amount: -1 * adjustmentLineItem.amount,
                                                status: 'Draft',
                                                reversal: {
                                                  lineItemId: adjustmentLineItem._id,
                                                },
                                              });
                                            } else {
                                              remove(rowIndex + 1);
                                            }
                                          }}
                                        >
                                          <Field
                                            name={name}
                                            component={Check}
                                            disabled={isSubmitting}
                                            checked={adjustmentLineItem.checked}
                                          />
                                        </DefaultButton>
                                      );
                                    },
                                  },
                                  {
                                    key: 'product',
                                    name: 'Product',
                                    minWidth: 170,
                                    headerClassName: styles.royaltiesEarnedColumn,
                                    onRender(adjustmentLineItem, rowIndex, column) {
                                      const name = getAdjustmentsFieldName(column, rowIndex);

                                      return (
                                        <Field
                                          name={name}
                                          component={ProductAutosuggest}
                                          disabled={
                                            isSubmitting ||
                                            mode === AdjustmentForm.MODE.REVERSE ||
                                            mode === AdjustmentForm.MODE.MINOR
                                          }
                                          underlined={false}
                                          required
                                          value={adjustmentLineItem.product?.inputValue || ''}
                                          hideSuggestions={!!adjustmentLineItem.product?.id}
                                          data-testid="adjustmentFormLineItemProduct"
                                          enablePfams
                                        />
                                      );
                                    },
                                  },
                                  values.contract?.silo?.toLowerCase() === '1p-azure' && {
                                    key: 'billToGeography',
                                    name: 'Bill To Country',
                                    minWidth: 150,
                                    headerClassName: styles.royaltiesEarnedColumn,
                                    onRender(item, rowIndex, column) {
                                      const name = getAdjustmentsFieldName(column, rowIndex);
                                      return (
                                        <Field
                                          name={name}
                                          component={GeographyTypeAhead}
                                          disabled={
                                            isSubmitting ||
                                            mode === AdjustmentForm.MODE.REVERSE ||
                                            mode === AdjustmentForm.MODE.MINOR
                                          }
                                          selectedValue={item.billToGeography}
                                          label=""
                                          dataTestId="adjustmentFormLineItemBillToGeography"
                                          dropdownLabelProps={{ style: { display: 'none' } }}
                                        />
                                      );
                                    },
                                  },
                                  {
                                    key: 'geography',
                                    name: 'Geography',
                                    minWidth: 150,
                                    headerClassName: styles.royaltiesEarnedColumn,
                                    onRender(item, rowIndex, column) {
                                      const name = getAdjustmentsFieldName(column, rowIndex);

                                      return (
                                        <Field
                                          name={name}
                                          component={GeographyTypeAhead}
                                          disabled={
                                            isSubmitting ||
                                            mode === AdjustmentForm.MODE.REVERSE ||
                                            mode === AdjustmentForm.MODE.MINOR
                                          }
                                          selectedValue={item.geography}
                                          label=""
                                          dataTestId="adjustmentFormLineItemGeography"
                                          dropdownLabelProps={{ style: { display: 'none' } }}
                                        />
                                      );
                                    },
                                  },
                                  {
                                    key: 'amount',
                                    name: 'Amount',
                                    headerClassName: styles.royaltiesEarnedColumn,
                                    onRender(item, rowIndex, column) {
                                      const name = getAdjustmentsFieldName(column, rowIndex);

                                      return (
                                        <Field
                                          name={name}
                                          component={TextField}
                                          ariaLabel={name}
                                          disabled={
                                            isSubmitting ||
                                            mode === AdjustmentForm.MODE.REVERSE ||
                                            mode === AdjustmentForm.MODE.MINOR
                                          }
                                          type="text"
                                          data-testid="adjustmentFormLineItemAmount"
                                          required
                                          inputClassName={styles.amountTextField}
                                          formatter={numWithCommasFormatter}
                                        />
                                      );
                                    },
                                  },
                                  {
                                    key: 'currency',
                                    name: 'Currency',
                                    fieldName: 'currency',
                                    minWidth: 250,
                                    headerClassName: styles.royaltiesEarnedColumn,
                                    onRender(item, rowIndex, column) {
                                      const name = getAdjustmentsFieldName(column, rowIndex);

                                      return (
                                        <Field
                                          name={name}
                                          component={CurrencyTypeAhead}
                                          disabled={
                                            isSubmitting ||
                                            mode === AdjustmentForm.MODE.REVERSE ||
                                            mode === AdjustmentForm.MODE.MINOR
                                          }
                                          label=""
                                          selectedValue={
                                            item.currency?.code && item.currency?.name
                                              ? {
                                                  key: item.currency,
                                                  text: `${item.currency.code} - ${item.currency.name}`,
                                                }
                                              : {
                                                  key: item.currency?.code,
                                                  text: item.currency?.code,
                                                }
                                          }
                                          dataTestId="adjustmentFormLineItemCurrency"
                                          dropdownLabelProps={{ style: { display: 'none' } }}
                                        />
                                      );
                                    },
                                  },
                                  {
                                    key: 'actualDate',
                                    name: 'Actual Date',
                                    minWidth: 190,
                                    headerClassName: styles.royaltiesEarnedColumn,
                                    onRender(lineItem, rowIndex, column) {
                                      const name = getAdjustmentsFieldName(column, rowIndex);
                                      const isReversalDraft =
                                        !!lineItem.reversal &&
                                        lineItem.status.toLowerCase() === 'draft';

                                      return (
                                        <Field
                                          name={name}
                                          component={DatePicker}
                                          allowTextInput
                                          disabled={
                                            isSubmitting ||
                                            (mode === AdjustmentForm.MODE.REVERSE &&
                                              !isReversalDraft) ||
                                            mode === AdjustmentForm.MODE.MINOR
                                          }
                                          data-testid="adjustmentFormLineItemDate"
                                          showClearButton
                                        />
                                      );
                                    },
                                  },
                                  {
                                    key: 'menu',
                                    name: 'Menu',
                                    minWidth: 40,
                                    maxWidth: 40,
                                    headerClassName: styles.royaltiesEarnedColumn,
                                    styles: { root: { visibility: 'hidden' } },
                                    onRender(item, rowIndex) {
                                      return (
                                        <IconButton
                                          iconProps={{ iconName: 'More' }}
                                          title="Options"
                                          ariaLabel="Options"
                                          onRenderMenuIcon={() => null}
                                          data-testid="adjustmentFormLineItemMenu"
                                          disabled={
                                            isSubmitting ||
                                            mode === AdjustmentForm.MODE.REVERSE ||
                                            mode === AdjustmentForm.MODE.MINOR
                                          }
                                          menuProps={{
                                            shouldFocusOnMount: false,
                                            items: [
                                              {
                                                key: 'duplicate',
                                                text: 'Duplicate',
                                                onClick: () =>
                                                  insert(rowIndex + 1, {
                                                    ...values.adjustments[rowIndex],
                                                  }),
                                              },
                                              {
                                                key: 'delete',
                                                text: 'Delete',
                                                onClick: () => remove(rowIndex),
                                                disabled: values.adjustments.length <= 1,
                                              },
                                            ],
                                          }}
                                        />
                                      );
                                    },
                                  },
                                ].filter(col => !!col)}
                              />

                              {mode !== AdjustmentForm.MODE.REVERSE &&
                                mode !== AdjustmentForm.MODE.MINOR && (
                                  <div className={styles.royaltiesEarnedButtonContainer}>
                                    <TextButton
                                      type="button"
                                      className={styles.addItem}
                                      onClick={() =>
                                        push(
                                          createNewAdjustmentLineItem({
                                            currency: { code: values.contract?.contractCurrency },
                                          }),
                                        )
                                      }
                                      data-testid="adjustmentFormAddItem"
                                    >
                                      Add Item
                                    </TextButton>
                                  </div>
                                )}
                            </>
                          )}
                        </FieldArray>
                      </SubModuleContent>
                    </SubModule>
                  </ModuleContent>
                </Module>
              )}

            {/* Advance Adjustment types Module (Recoupable Payments, NonRecoupable and Advance Balance) */}
            {['RecoupablePayment', 'NonRecoupablePayment', 'AdvanceBalance'].includes(
              values.adjustmentType,
            ) &&
              values.contract?.contractId && (
                <Module>
                  <ModuleContent>
                    <SubModule disableAccordian closed data-testid="advanceAdjustmentSection">
                      <SubModuleHeader
                        variant="large"
                        extra={getAdvancePaymentTotalString(
                          values[getAdjustmentListItemsName(values.adjustmentType)],
                          styles,
                        )}
                      >
                        {startCase(values.adjustmentType)}
                      </SubModuleHeader>
                    </SubModule>
                  </ModuleContent>
                  <ModuleContent>
                    <FieldArray name={getAdjustmentListItemsName(values.adjustmentType)}>
                      {({ push, insert, remove }) => {
                        const adjustmentListItemsName = getAdjustmentListItemsName(
                          values.adjustmentType,
                        );
                        return (
                          <>
                            {values[adjustmentListItemsName].map((recoupablePayment, index) => {
                              const selectedCurrency =
                                recoupablePayment.currency?.code && recoupablePayment.currency?.name
                                  ? {
                                      key: recoupablePayment.currency.code,
                                      text: `${recoupablePayment.currency.code} - ${recoupablePayment.currency.name}`,
                                    }
                                  : {
                                      key: recoupablePayment.currency?.code
                                        ? recoupablePayment.currency.code
                                        : recoupablePayment.currency,
                                      text: recoupablePayment.currency?.code
                                        ? recoupablePayment.currency.code
                                        : recoupablePayment.currency,
                                    };

                              const setStatementPeriod = date => {
                                const stmtPeriodField = `${adjustmentListItemsName}.${index}.statementPeriod`;
                                const lastDayOfMonth = new Date(
                                  moment(date).endOf('month').format('YYYY-MM-DD'),
                                );
                                setFieldValue(stmtPeriodField, lastDayOfMonth);
                                setFieldTouched(stmtPeriodField);
                              };

                              const handleInvoiceDateChange = (name, selectedDate) => {
                                setStatementPeriod(selectedDate);
                              };

                              const onSelectStatementDate = date => {
                                setStatementPeriod(date);
                              };

                              const formatDatePickerDate = date => {
                                return moment(date).format('MM/YYYY');
                              };

                              return (
                                <SubModule
                                  key={
                                    /* eslint-disable react/no-array-index-key */ `recoupablePayment-${index}`
                                  }
                                  data-testid="paymentSubModule"
                                  className={styles.paymentSection}
                                >
                                  <SubModuleHeader variant="large" id={`Payment-${index + 1}`}>
                                    {values.adjustmentType === 'AdvanceBalance'
                                      ? `Adjustment #${index + 1}`
                                      : `Payment #${index + 1}`}
                                  </SubModuleHeader>
                                  <SubModuleContent>
                                    <StackGrid smCols={1} mdCols={3} lgCols={3} shrink>
                                      {values.adjustmentType === 'AdvanceBalance' ? (
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'adjustmentDate',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Adjustment Date"
                                          component={DatePicker}
                                          className={styles.dateField}
                                          required
                                          allowTextInput
                                          data-testid="adjustmentDate"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      ) : (
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'invoiceNumber',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Invoice Number"
                                          component={TextField}
                                          maxLength={16}
                                          required
                                          data-testid="paymentInvoiceNumber"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      )}
                                      <Field
                                        name={getAdvanceAdjustmentFieldName(
                                          'amount',
                                          index,
                                          adjustmentListItemsName,
                                        )}
                                        label="Amount"
                                        component={TextField}
                                        required
                                        data-testid="paymentAmount"
                                        formatter={numWithCommasFormatter}
                                        allowNegative={values.adjustmentType === 'AdvanceBalance'}
                                        disabled={
                                          isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                        }
                                      />
                                      <Field
                                        name={getAdvanceAdjustmentFieldName(
                                          'currency',
                                          index,
                                          adjustmentListItemsName,
                                        )}
                                        component={CurrencyTypeAhead}
                                        selectedValue={selectedCurrency}
                                        label="Currency"
                                        dataTestId="paymentCurrency"
                                        required
                                        className={styles.noBg}
                                        dropdownLabelProps={{ style: { display: 'none' } }}
                                        disabled={
                                          isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                        }
                                      />
                                    </StackGrid>
                                    <StackGrid
                                      smCols={1}
                                      mdCols={3}
                                      lgCols={3}
                                      className={styles.fieldStackGrids}
                                      shrink
                                    >
                                      {values.adjustmentType !== 'AdvanceBalance' && (
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'invoiceDate',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Invoice Date"
                                          component={DatePicker}
                                          onChange={handleInvoiceDateChange}
                                          allowTextInput
                                          required
                                          data-testid="paymentInvoiceDate"
                                          className={styles.dateField}
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      )}

                                      {values.adjustmentType !== 'AdvanceBalance' && (
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'dueDate',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Due Date"
                                          component={DatePicker}
                                          allowTextInput
                                          className={styles.dateField}
                                          required
                                          data-testid="paymentDueDate"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      )}

                                      {values.adjustmentType === 'RecoupablePayment' && (
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'statementPeriod',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Statement Period"
                                          component={DatePicker}
                                          calendarAs={() => (
                                            <Calendar
                                              dateRangeType={DateRangeType.Month}
                                              isDayPickerVisible={false}
                                              isMonthPickerVisible
                                              onSelectDate={onSelectStatementDate}
                                              strings={MonthPickerStrings}
                                            />
                                          )}
                                          formatDate={formatDatePickerDate}
                                          className={styles.dateField}
                                          allowTextInput
                                          required
                                          data-testid="statementPeriod"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      )}
                                    </StackGrid>
                                    <Stack
                                      smCols={1}
                                      mdCols={3}
                                      lgCols={3}
                                      className={styles.fieldStackGrids}
                                      horizontal
                                      gap={values.adjustmentType !== 'AdvanceBalance' ? 10 : 120}
                                    >
                                      {values.adjustmentType !== 'AdvanceBalance' && (
                                        <Stack.Item grow>
                                          <Field
                                            name={getAdvanceAdjustmentFieldName(
                                              'taxCode',
                                              index,
                                              adjustmentListItemsName,
                                            )}
                                            label="Tax Code"
                                            data-testid="taxCode"
                                            options={taxCodes}
                                            component={Dropdown}
                                            required
                                            className={styles.taxCode}
                                            disabled={
                                              isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                            }
                                          />
                                        </Stack.Item>
                                      )}

                                      {values.adjustmentType === 'AdvanceBalance' && (
                                        <Stack.Item
                                          className={styles.advanceBalanceStmtPeriod}
                                          grow={1}
                                        >
                                          <Field
                                            name={getAdvanceAdjustmentFieldName(
                                              'statementPeriod',
                                              index,
                                              adjustmentListItemsName,
                                            )}
                                            label="Statement Period"
                                            component={DatePicker}
                                            className={styles.dateField}
                                            calendarAs={() => (
                                              <Calendar
                                                dateRangeType={DateRangeType.Month}
                                                isDayPickerVisible={false}
                                                isMonthPickerVisible
                                                onSelectDate={onSelectStatementDate}
                                                strings={MonthPickerStrings}
                                              />
                                            )}
                                            formatDate={formatDatePickerDate}
                                            allowTextInput
                                            required
                                            data-testid="statementPeriod"
                                            disabled={
                                              isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                            }
                                          />
                                        </Stack.Item>
                                      )}

                                      <Stack.Item grow={5}>
                                        <Stack horizontal verticalAlign="start">
                                          <Label>Product</Label>
                                        </Stack>
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'product',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Product"
                                          component={ProductAutosuggest}
                                          enablePfams
                                          className={styles.noBg}
                                          required
                                          data-testid="paymentProduct"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      </Stack.Item>
                                    </Stack>

                                    <StackGrid
                                      className={styles.fieldStackGrids}
                                      smCols={1}
                                      mdCols={3}
                                      lgCols={3}
                                      shrink
                                    >
                                      {values.adjustmentType === 'NonRecoupablePayment' && (
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'expenseAccount',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Expense Account"
                                          component={TextField}
                                          required
                                          data-testid="expenseAccount"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      )}

                                      {values.adjustmentType === 'NonRecoupablePayment' && (
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'costCenter',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Cost Center/Internal Order"
                                          component={TextField}
                                          required
                                          data-testid="costCenter"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      )}
                                    </StackGrid>
                                    <StackGrid smCols={1} mdCols={1} lgCols={1} shrink>
                                      <Field
                                        name={getAdvanceAdjustmentFieldName(
                                          'description',
                                          index,
                                          adjustmentListItemsName,
                                        )}
                                        label="Description"
                                        component={TextField}
                                        multiline
                                        data-testid="paymentDescription"
                                        className={styles.fieldStackGrids}
                                        disabled={
                                          isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                        }
                                      />
                                      {values.adjustmentType !== 'NonRecoupablePayment' && (
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'statementComment',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Statement Comment"
                                          callout="Statement comments will be visible to partners."
                                          component={TextField}
                                          multiline
                                          data-testid="statementComment"
                                          className={styles.adjustmentName}
                                        />
                                      )}
                                    </StackGrid>

                                    {values.adjustmentType !== 'AdvanceBalance' && (
                                      <StackGrid
                                        smCols={1}
                                        mdCols={3}
                                        lgCols={3}
                                        shrink
                                        parentClassName={styles.fieldStackGrids}
                                      >
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'invoiceApprover',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Invoice Approver"
                                          component={GraphUsersTypeAhead}
                                          dataTestId="paymentInvoiceApprover"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                        <Field
                                          name={getAdvanceAdjustmentFieldName(
                                            'interimInvoiceApprover',
                                            index,
                                            adjustmentListItemsName,
                                          )}
                                          label="Interim Invoice Approver"
                                          component={GraphUsersTypeAhead}
                                          dataTestId="paymentInterimInvoiceApprover"
                                          disabled={
                                            isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                          }
                                        />
                                      </StackGrid>
                                    )}

                                    <div className={styles.royaltiesEarnedButtonContainer}>
                                      <TextButton
                                        type="button"
                                        className={styles.addItem}
                                        data-testid="duplicatePayment"
                                        onClick={() =>
                                          insert(index + 1, {
                                            ...values[adjustmentListItemsName][index],
                                          })
                                        }
                                        disabled={
                                          isSubmitting || mode === AdjustmentForm.MODE.MINOR
                                        }
                                      >
                                        {values.adjustmentType === 'AdvanceBalance'
                                          ? `Duplicate Adjustment`
                                          : `Duplicate Payment`}
                                      </TextButton>
                                      {values[adjustmentListItemsName].length > 1 && (
                                        <TextButton
                                          type="button"
                                          className={styles.addItem}
                                          data-testid="deletePayment"
                                          onClick={() => remove(index)}
                                        >
                                          {values.adjustmentType === 'AdvanceBalance'
                                            ? `Delete Adjustment`
                                            : `Delete Payment`}
                                        </TextButton>
                                      )}
                                    </div>
                                  </SubModuleContent>
                                </SubModule>
                              );
                            })}

                            {mode !== AdjustmentForm.MODE.MINOR && (
                              <SubModule disableAccordian closed>
                                <SubModuleHeader
                                  variant="large"
                                  data-testid="addPayment"
                                  extra={
                                    <IconButton
                                      iconProps={{ iconName: 'Add' }}
                                      title="Add a Payment"
                                      ariaLabel="Add a Payment"
                                    />
                                  }
                                  onClick={() =>
                                    push(
                                      createNewAdjustmentLineOrPaymentItem(values.adjustmentType, {
                                        currency: { code: values.contract.contractCurrency },
                                      }),
                                    )
                                  }
                                >
                                  {values.adjustmentType === 'AdvanceBalance'
                                    ? `Add an Adjustment`
                                    : `Add a Payment`}
                                </SubModuleHeader>
                              </SubModule>
                            )}
                          </>
                        );
                      }}
                    </FieldArray>
                  </ModuleContent>
                </Module>
              )}

            {/* Advance group section */}
            {['RecoupablePayment', 'AdvanceBalance'].includes(values.adjustmentType) &&
              values.contract?.contractId &&
              mode !== AdjustmentForm.MODE.MINOR && (
                <Module data-testid="adjustmentsFormAdvanceGroupSection">
                  <ModuleContent>
                    <SubModule disableAccordian>
                      <SubModuleHeader variant="large">Recoupment Terms</SubModuleHeader>
                      <SubModuleContent>
                        <StackGrid smCols={1} mdCols={3} lgCols={3} shrink>
                          <Field
                            name="advanceGroup"
                            label="Advance Group"
                            component={Dropdown}
                            options={advanceGroupOptions}
                            handleCallback={advanceGroupChangeCallback}
                            disabled={isSubmitting || mode === AdjustmentForm.MODE.MINOR}
                            required
                            data-testid="adjustmentFormAdvanceGroupId"
                          />
                          {values.advanceAccount && (
                            <Stack>
                              <Stack.Item grow={2} shrink={2}>
                                <Stack gap={10}>
                                  <Text>Advance Account</Text>
                                  <Text className={styles.contractId}>{values.advanceAccount}</Text>
                                </Stack>
                              </Stack.Item>
                            </Stack>
                          )}

                          {values.advanceAccount && (
                            <div className={styles.viewAdvanceGroup}>
                              <Link href={`/advancegroups/${values.advanceGroup}`} target="_blank">
                                View
                              </Link>
                            </div>
                          )}
                        </StackGrid>
                        <div className={styles.royaltiesEarnedButtonContainer}>
                          <Link
                            className={styles.addAdvanceGroup}
                            href="/add-advanceGroup"
                            target="_blank"
                          >
                            Create New Advance Group
                          </Link>
                        </div>
                      </SubModuleContent>
                    </SubModule>
                  </ModuleContent>
                </Module>
              )}

            {/* Reviewer Module */}
            {values.adjustmentType &&
              values.contract?.contractId &&
              mode !== AdjustmentForm.MODE.MINOR && (
                <Module data-testid="adjustmentsFormReviewerSection">
                  <ModuleContent>
                    <SubModule disableAccordian>
                      <SubModuleHeader variant="large">Reviewer</SubModuleHeader>
                      <SubModuleContent>
                        <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                          <Field
                            name="reviewer.reviewerId"
                            label="Reviewer"
                            component={Dropdown}
                            placeholder="None"
                            options={reviewers}
                            disabled={isSubmitting || mode === AdjustmentForm.MODE.MINOR}
                            data-testid="adjustmentFormReviewerName"
                          />

                          <Field
                            name="reviewer.reviewerJustification"
                            label="Reviewer Justification"
                            component={TextField}
                            disabled={isSubmitting || mode === AdjustmentForm.MODE.MINOR}
                            multiline
                            data-testid="adjustmentFormReviewerJustification"
                          />
                        </StackGrid>
                      </SubModuleContent>
                    </SubModule>
                  </ModuleContent>
                </Module>
              )}

            {/* Approver Module (Used only for advance adjustment types) */}
            {values.adjustmentType &&
              values.contract?.contractId &&
              mode !== AdjustmentForm.MODE.MINOR && (
                <Module data-testid="adjustmentsFormApproverSection">
                  <ModuleContent>
                    <SubModule disableAccordian>
                      <SubModuleHeader variant="large">Approver</SubModuleHeader>
                      <SubModuleContent>
                        <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                          <Field
                            name="approver.approverId"
                            label="Approver"
                            initialValue={getApprover(values)}
                            component={Dropdown}
                            options={
                              isPaymentAmountGreaterThanThreshold(values)
                                ? advanceAdjustmentRomApprovers
                                : advanceAdjustmentCpmApprovers
                            }
                            disabled={isSubmitting || mode === AdjustmentForm.MODE.MINOR}
                            required
                            data-testid="adjustmentFormApproverName"
                          />

                          <Field
                            name="approver.approverJustification"
                            label="Approver Justification"
                            required={
                              values.approver?.approverId !== getApproverAssignedToContract(values)
                            }
                            component={TextField}
                            disabled={isSubmitting || mode === AdjustmentForm.MODE.MINOR}
                            multiline
                            data-testid="adjustmentFormApproverJustification"
                          />
                        </StackGrid>
                      </SubModuleContent>
                    </SubModule>
                  </ModuleContent>
                </Module>
              )}
          </Form>
        )}
      </Formik>
    );
  },
);

AdjustmentForm.MODE = {
  CREATE: 'CREATE',
  REVERSE: 'REVERSE',
  EDIT: 'EDIT',
  MINOR: 'MINOR', // minor changes do not require review
  COPY: 'COPY',
};

AdjustmentForm.propTypes = {
  adjustmentId: PropTypes.string,
  onSubmit: PropTypes.func,
  onSave: PropTypes.func,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  mode: PropTypes.oneOf(Object.values(AdjustmentForm.MODE)),
  contractAddress: PropTypes.string,
};

AdjustmentForm.defaultProps = {
  adjustmentId: null,
  onSubmit: null,
  onSave: null,
  mode: AdjustmentForm.MODE.CREATE,
  onSuccess: () => {},
  onFailure: () => {},
  contractAddress: null,
};

export default AdjustmentForm;
